import { getApiUrl } from '../environment'
import checkmateService from '../checkmate'

// Inlined to avoid circular dependency with ../auth which imports baseApiService
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() ?? null
  return null
}

export type QueryParams = Record<string, string | number | boolean | string[] | number[] | undefined | null>

export interface FetchOptions extends Omit<RequestInit, 'body'> {
  params?: QueryParams
  body?: BodyInit | Record<string, unknown> | null
  /**
   * When true, bypasses c_selections and uses tenantUuid instead
   * (matches legacy disabledCSelections behavior)
   */
  disableCSelections?: boolean
  /**
   * When true, forces direct connection to RCM backend (implies disableCSelections)
   * (matches legacy directConnection behavior)
   */
  directConnection?: boolean
  /**
   * Override tenantUuid value (matches legacy configuration.tenantUuid)
   */
  tenantUuid?: string
  /**
   * Override base URL for this request
   * (matches legacy configuration.baseUrl which replaces the entire base URL)
   */
  baseUrl?: string
}

export class ApiError extends Error {
  public status: number
  public statusText: string
  public body?: unknown

  constructor(status: number, statusText: string, message: string, body?: unknown) {
    super(message)
    this.name = ''
    this.status = status
    this.statusText = statusText
    this.body = body
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

export class BaseApiService {
  private readonly baseUrl: string
  private readonly cSelections: string
  private readonly isDevelopment: boolean

  /**
   * Public getter for baseUrl (for logging/debugging only)
   */
  public getBaseUrl(): string {
    return this.baseUrl
  }

  constructor(baseUrl?: string) {
    // Determine baseUrl using checkmateService
    const isCheckmated = checkmateService.isCheckmated()
    let selectedBaseUrl: string
    if (isCheckmated) {
      selectedBaseUrl = checkmateService.getCheckmatedApiUrl()
    } else {
      // Always use getApiUrl() for fallback, which is the correct default
      selectedBaseUrl = getApiUrl()
    }
    this.baseUrl = baseUrl ?? selectedBaseUrl
    // Logging for debugging
    console.log('[BaseApiService.constructor] checkmateService.isCheckmated():', isCheckmated)
    // Detect if running in development
    this.isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    // Only handle c_selections in production (not localhost)
    if (!this.isDevelopment) {
      const urlParams = new URLSearchParams(window.location.search)
      const cSelectionsFromUrl = urlParams.get('c_selections')

      if (cSelectionsFromUrl) {
        localStorage.setItem('c_selections', cSelectionsFromUrl)
        this.cSelections = cSelectionsFromUrl
      } else {
        this.cSelections = localStorage.getItem('c_selections') ?? ''
      }
    } else {
      this.cSelections = ''
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest'
    }

    // Add XSRF token to headers (matches legacy restangular.config.js)
    const xsrfToken = getCookie('XSRF-TOKEN')
    if (xsrfToken) {
      headers['X-XSRF-TOKEN'] = xsrfToken
    }

    return headers
  }

  private formatQueryParams(params: QueryParams): string {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return

      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)))
      } else {
        searchParams.append(key, String(value))
      }
    })

    return searchParams.toString()
  }

  async fetch<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    let finalEndpoint = endpoint

    // Add XSRF token to query params (matches legacy restangular.config.js)
    const xsrfToken = getCookie('XSRF-TOKEN')
    const paramsWithXsrf = options?.params ? { ...options.params } : {}
    if (xsrfToken) {
      paramsWithXsrf['_csrf'] = xsrfToken
    }

    if (Object.keys(paramsWithXsrf).length > 0) {
      const queryString = this.formatQueryParams(paramsWithXsrf)
      if (queryString) {
        const separator = endpoint.includes('?') ? '&' : '?'
        finalEndpoint = `${endpoint}${separator}${queryString}`
      }
    }

    // Determine if we should disable c_selections
    // Legacy logic: if (!configuration.disabledCSelections && isCheckmated) use c_selections, else use tenantUuid
    const shouldDisableCSelections =
      this.isDevelopment || options?.disableCSelections === true || options?.directConnection === true

    // Always use latest checkmateService.isCheckmated() value
    const isCheckmated = checkmateService.isCheckmated()
    const tenantUuid = options?.tenantUuid ?? localStorage.getItem('tenantUuid') ?? localStorage.getItem('tenant')

    if (!shouldDisableCSelections && isCheckmated && this.cSelections) {
      // Use c_selections when checkmated AND not disabled
      const separator = finalEndpoint.includes('?') ? '&' : '?'
      const endpointWithSelections = finalEndpoint.includes('c_selections=')
        ? finalEndpoint
        : `${finalEndpoint}${separator}c_selections=${this.cSelections}`

      finalEndpoint = endpointWithSelections
    } else {
      // Only append tenantUuid for non-checkmated instance
      if (tenantUuid && !finalEndpoint.includes('tenantUuid=')) {
        const separator = finalEndpoint.includes('?') ? '&' : '?'
        finalEndpoint = `${finalEndpoint}${separator}tenantUuid=${tenantUuid}`
      }
    }

    // Use override baseUrl if provided, otherwise use instance baseUrl
    // This matches legacy behavior where configuration.baseUrl replaces the entire base URL
    const effectiveBaseUrl = options?.baseUrl ?? this.baseUrl
    const url = `${effectiveBaseUrl}${finalEndpoint}`

    try {
      const authHeaders = this.getAuthHeaders()

      // Don't set Content-Type for FormData - browser will set it with boundary
      const isFormData = options?.body instanceof FormData

      const headers: Record<string, string> = {
        ...authHeaders,
        ...((options?.headers as Record<string, string>) || {})
      }

      // Remove undefined Content-Type and only set for non-FormData
      if (headers['Content-Type'] === undefined) {
        delete headers['Content-Type']
      }

      if (!isFormData && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json'
      }

      const fetchOptions: RequestInit = {
        ...options,
        credentials: 'include',
        headers,
        body:
          options?.body &&
          typeof options.body === 'object' &&
          !(options.body instanceof Blob) &&
          !(options.body instanceof ArrayBuffer) &&
          !(options.body instanceof FormData) &&
          !(options.body instanceof URLSearchParams) &&
          !(options.body instanceof ReadableStream)
            ? JSON.stringify(options.body)
            : (options?.body as BodyInit | null | undefined)
      }

      delete (fetchOptions as Record<string, unknown>).params

      const response = await fetch(url, fetchOptions)

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null)
        // Try to extract a meaningful error message from the response body
        let errorMessage = `API error: ${response.status} ${response.statusText}`
        if (errorBody && typeof errorBody === 'object') {
          if (typeof errorBody.message === 'string' && errorBody.message.trim()) {
            errorMessage = errorBody.message
          } else if (typeof errorBody.error === 'string' && errorBody.error.trim()) {
            errorMessage = errorBody.error
          }
        }
        throw new ApiError(response.status, response.statusText, errorMessage, errorBody)
      }

      // Try to parse JSON, return empty object if response is empty or not JSON
      try {
        return (await response.json()) as T
      } catch {
        return {} as T
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      console.error(`Error fetching from ${url}:`, error)
      throw error
    }
  }
}

export const baseApiService = new BaseApiService()
